"use client";

import { RiAppleFill, RiLiveLine, RiSoundcloudFill } from "@remixicon/react";
import type { ProcessedSet, ProgramSetData } from "@workspace/types";
import { getB2BDisplayParts, parseGenres } from "@workspace/types";
import { cn } from "@workspace/ui/lib/utils";
import {
	type ComponentProps,
	Fragment,
	memo,
	type ReactNode,
	useCallback,
} from "react";
import { useUserPreferences } from "@/hooks/shared/use-user-preferences";
import {
	formatDayAndTime,
	formatStageName,
} from "@/lib/business-logic/set-rendering";
import { createArtistSlug } from "@/lib/features/artist/artist-utils";
import { ClickableArtistPhoto } from "./photos/clickable-artist-photo";
import { ClickableFestivalPoster } from "./photos/clickable-festival-poster";
import type { FestivalPoster } from "./photos/festival-poster";
import { SocialButtonGroup } from "./social-button-group";

// Helper to determine which recording icon to show based on available URLs
const getRecordingIcon = (set: ProcessedSet) => {
	const hasYoutube = !!set.youtube_url;
	const hasSoundcloud = !!set.soundcloud_url;
	const hasAppleMusic = !!set.apple_music_url;

	const sourceCount = [hasYoutube, hasSoundcloud, hasAppleMusic].filter(Boolean).length;

	if (sourceCount > 1 || hasYoutube) {
		return RiLiveLine;
	}
	if (hasSoundcloud) {
		return RiSoundcloudFill;
	}
	if (hasAppleMusic) {
		return RiAppleFill;
	}
	return null;
};

// Type for artist with social links
interface ArtistWithSocials {
	id?: string;
	name: string;
	image_url?: string | null;
	spotify_url?: string | null;
	youtube_url?: string | null;
	instagram?: string | null;
	twitter?: string | null;
	facebook?: string | null;
	tiktok?: string | null;
	soundcloud?: string | null;
	website_url?: string | null;
	store?: string | null;
}

interface SetInfoDisplayProps {
	set: ProcessedSet;
	className?: string;
	showStage?: boolean;
	showTime?: boolean;
	showLiveIndicator?: boolean;
	showGenres?: boolean;
	titleElement?: "h3" | "h4" | "p" | "div";
	titleClassName?: string;
	subtitleClassName?: string;
	photoSize?: ComponentProps<typeof FestivalPoster>["size"];
	photoRounded?: ComponentProps<typeof FestivalPoster>["rounded"];
	textBreakingClass?: string;
	renderTitle?: (set: ProcessedSet) => ReactNode;
	renderSubtitle?: (set: ProcessedSet) => ReactNode;
	context?: "card" | "modal" | "detail";
	orientation?: "horizontal" | "vertical";
	showSocialButtons?: boolean;
	priority?: boolean;
	showPoster?: boolean;
	photoType?: "artist" | "festival";
	disablePhotoLink?: boolean;
}

/**
 * Reusable component for displaying set information with artist photo,
 * name, stage, time, and optional elements like genres or live indicators.
 *
 * Used across SetCard, MyListCard, SetReviewModal, SetDetailCard, etc.
 */
export const SetInfoDisplay = memo(function SetInfoDisplay({
	set,
	className,
	showStage = true,
	showTime = true,
	showLiveIndicator = false,
	showGenres = false,
	titleElement: TitleElement = "h3",
	titleClassName = "font-semibold",
	photoSize = "sm",
	photoRounded = "lg",
	textBreakingClass = "break-words",
	renderTitle,
	renderSubtitle,
	context = "card",
	orientation = "horizontal",
	showSocialButtons = false,
	priority = false,
	showPoster = true,
	photoType = "festival",
	disablePhotoLink = false,
}: SetInfoDisplayProps) {
	// Get user preferences for time/date formatting
	const userPreferences = useUserPreferences();

	// Get festival poster URL for sets
	const getFestivalPosterUrl = () => {
		const festivalData = set as ProcessedSet & {
			festival?: { poster_url?: string };
		};
		return festivalData.festival?.poster_url;
	};

	// Get festival profile URL for navigation
	const getFestivalProfileUrl = useCallback(() => {
		const festivalData = set as ProcessedSet & {
			festival?: { name?: string; slug?: string };
		};

		// Don't link if no festival
		if (!festivalData.festival?.name || !festivalData.festival?.slug) {
			return null;
		}

		return `/festivals/${festivalData.festival.slug}`;
	}, [set]);

	const defaultTitleRenderer = () => {
		// For surprise sets, ALWAYS show placeholder in card/detail context to preserve the mystery.
		if (set.is_surprise && context !== "modal") {
			return set.placeholder_name || "Surprise Guest";
		}

		// Otherwise, always use the detailed B2B display logic.
		const displayParts = getB2BDisplayParts(
			set,
			context === "modal" ? "review" : undefined,
		);

		// For sets with multiple parts (3+ artists, or complex aliases)
		if (displayParts.parts && displayParts.parts.length > 0) {
			return (
				<>
					{displayParts.parts.map((part, index) => (
						<Fragment key={index}>
							{part.type === "artist" || part.type === "presents" ? (
								part.text
							) : (
								<span className="font-normal text-muted-foreground/50">
									{part.text}
								</span>
							)}
							{index < (displayParts.parts?.length ?? 0) - 1 && " "}
						</Fragment>
					))}
					{set.is_group_alias &&
						displayParts.parts?.some((part) => part.realText) && (
							<span className="ml-1 font-normal text-muted-foreground/50">
								{/* For group aliases, show all real artist names since the main text is the group alias */}
								{(() => {
									const allRealNames =
										displayParts.parts
											?.map(
												(part) =>
													part.realText ||
													(part.type === "artist" ? part.text : null),
											)
											.filter(Boolean) || [];
									return allRealNames.length > 0
										? `(${allRealNames.join(" & ")})`
										: null;
								})()}
							</span>
						)}
				</>
			);
		}

		// For all other sets (single artist, 2-artist B2B, presents, etc.)
		return (
			<>
				{displayParts.mainText}
				{displayParts.connector && (
					<span className="ml-1 font-normal text-muted-foreground/50">
						{displayParts.connector}
					</span>
				)}
				{displayParts.secondaryText && (
					<span className="ml-1">{displayParts.secondaryText}</span>
				)}
				{set.is_group_alias && displayParts.realMainText && (
					<span className="ml-1 font-normal text-muted-foreground/50">
						{/* For group aliases, show all real artist names since the main text is the group alias */}
						{`(${displayParts.realMainText})`}
					</span>
				)}
			</>
		);
	};

	const defaultSubtitleRenderer = () => {
		const hasStage = showStage;
		const hasTime = showTime;
		const genres =
			showGenres && set.artist
				? parseGenres(
						set.artist.spotify_genres || null,
						set.artist.manual_genres || null,
					)
				: [];
		const hasGenres = genres.length > 0;

		// Don't render anything if there's no content to show
		if (!hasStage && !hasTime && !hasGenres) {
			return null;
		}

		return (
			<div>
				{hasStage && (
					<div className="text-muted-foreground text-xs">
						{formatStageName(set)}
					</div>
				)}

				{hasTime && (
					<div className="flex items-center gap-1 text-muted-foreground text-xs">
						<span>{formatDayAndTime(set, userPreferences)}</span>
						{showLiveIndicator && (() => {
							const RecordingIcon = getRecordingIcon(set);
							return RecordingIcon ? <RecordingIcon className="h-3 w-3 text-muted-foreground" /> : null;
						})()}
					</div>
				)}

				{hasGenres && (
					<div className="text-muted-foreground/80 text-xs">
						{genres.slice(0, 2).join(", ")}
					</div>
				)}
			</div>
		);
	};

	// Render artist or festival photo based on photoType
	const renderPhoto = () => {
		// Check if this is a program set first
		const programSet = "program_set_id" in set && set.program_set_id;
		const programSetData =
			"_program_set" in set
				? (set as ProcessedSet & { _program_set?: ProgramSetData })._program_set
				: null;

		if (photoType === "artist") {
			// For program sets, show calendar icon placeholder
			if (programSet || programSetData) {
				return (
					<ClickableArtistPhoto
						href={null}
						name={programSetData?.name || "Program"}
						imageUrl={null}
						size={photoSize === "full" ? "xl" : photoSize}
						priority={priority}
						rounded="card"
						isProgram={true}
					/>
				);
			}

			// For surprise sets without artist data, show surprise placeholder
			if (set.is_surprise && !set.artist) {
				return (
					<ClickableArtistPhoto
						href={null}
						name={set.placeholder_name || "Surprise Guest"}
						imageUrl={null}
						size={photoSize === "full" ? "xl" : photoSize}
						priority={priority}
						rounded="card"
						isSurprise={true}
					/>
				);
			}

			// For regular artist sets
			if (set.artist) {
				// Check if this is a group alias and route accordingly
				let profileUrl: string;
				if (set.is_group_alias && set.group_alias_slug) {
					profileUrl = `/groups/${set.group_alias_slug}`;
				} else {
					const artistSlug = createArtistSlug(set.artist.name, set.artist.slug);
					profileUrl = `/artists/${artistSlug}`;
				}

				return (
					<ClickableArtistPhoto
						href={disablePhotoLink ? null : profileUrl}
						name={set.artist.name}
						imageUrl={set.artist.image_url}
						size={photoSize === "full" ? "xl" : photoSize}
						priority={priority}
						rounded="card"
						isSurprise={set.is_surprise}
						trackingData={{
							artistId: set.artist.id,
							artistName: set.artist.name,
							source: context === "modal" ? "festival" : "search",
						}}
					/>
				);
			}

			// For empty/placeholder sets without artist data, show artist photo with mic icon fallback
			return (
				<ClickableArtistPhoto
					href={null}
					name="TBA"
					imageUrl={null}
					size={photoSize === "full" ? "xl" : photoSize}
					priority={priority}
					rounded="card"
					isSurprise={false}
				/>
			);
		}

		// Default to festival poster
		const festivalProfileUrl = getFestivalProfileUrl();

		const festivalData = set as ProcessedSet & {
			festival?: { id?: string; name?: string; poster_url?: string };
		};

		return (
			<ClickableFestivalPoster
				href={festivalProfileUrl}
				name={festivalData.festival?.name || "Festival"}
				posterUrl={getFestivalPosterUrl()}
				size={photoSize}
				priority={priority}
				rounded={photoRounded}
				trackingData={
					festivalData.festival?.id && festivalData.festival?.name
						? {
								festivalId: festivalData.festival.id,
								festivalName: festivalData.festival.name,
								source: "artist",
							}
						: undefined
				}
			/>
		);
	};

	if (orientation === "vertical") {
		return (
			<div className={cn("flex h-full flex-col", className)}>
				{/* Photo - full width */}
				{showPoster && (
					<div className="relative aspect-[3/4] w-full flex-shrink-0 overflow-hidden rounded-lg">
						{renderPhoto()}
					</div>
				)}

				{/* Info section below photo */}
				<div
					className={cn(
						"flex min-h-0 flex-1 flex-col justify-between",
						showPoster ? "pt-3" : "",
					)}
				>
					<div>
						{/* Use the same title styling as horizontal */}
						<TitleElement
							className={cn("leading-tight", titleClassName, textBreakingClass)}
						>
							{renderTitle ? renderTitle(set) : defaultTitleRenderer()}
						</TitleElement>
						{/* Subtitle section - matches horizontal layout */}
						{renderSubtitle ? renderSubtitle(set) : defaultSubtitleRenderer()}
					</div>
				</div>
			</div>
		);
	}

	return (
		<div
			className={cn("flex items-start", showPoster ? "gap-3" : "", className)}
		>
			{/* Photo */}
			{showPoster && renderPhoto()}

			{/* Title and Info */}
			<div className="min-w-0 flex-1">
				<div
					className={cn(
						"flex items-start justify-between gap-2",
						showSocialButtons && "pr-2",
					)}
				>
					<div className="min-w-0 flex-1">
						<TitleElement
							className={cn(
								"line-clamp-2 leading-tight",
								titleClassName,
								textBreakingClass,
							)}
						>
							{renderTitle ? renderTitle(set) : defaultTitleRenderer()}
						</TitleElement>
						{(() => {
							const subtitle = renderSubtitle
								? renderSubtitle(set)
								: defaultSubtitleRenderer();
							return subtitle;
						})()}
					</div>

					{/* Social buttons in top right */}
					{showSocialButtons && set.artist && (
						<SocialButtonGroup
							name={set.artist.name}
							links={{
								spotify: (set.artist as ArtistWithSocials)?.spotify_url,
								youtube: (set.artist as ArtistWithSocials)?.youtube_url,
								instagram: (set.artist as ArtistWithSocials)?.instagram,
								twitter: (set.artist as ArtistWithSocials)?.twitter,
								facebook: (set.artist as ArtistWithSocials)?.facebook,
								tiktok: (set.artist as ArtistWithSocials)?.tiktok,
								soundcloud: (set.artist as ArtistWithSocials)?.soundcloud,
								website: (set.artist as ArtistWithSocials)?.website_url,
								store: (set.artist as ArtistWithSocials)?.store,
							}}
							size="xs"
						/>
					)}
				</div>
			</div>
		</div>
	);
});

SetInfoDisplay.displayName = "SetInfoDisplay";
